import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { FacebookIcon, MessageCircleIcon, SendIcon, YoutubeIcon, CheckCircleIcon, PlugIcon, InstagramIcon, StoreIcon, TruckIcon, TrashIcon, CalendarIcon } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { showSuccess, showError } from '@/lib/toast-helpers'

interface InstagramAccount {
  business_type: string
  username: string
  account_type: string
  profile_picture_url: string | null
  token_expires_at: string
  conversations_count: number
}

interface MessengerConnection {
  business_type: string
  page_name: string
  page_username: string | null
  profile_picture_url: string | null
  token_expires_at: string
  conversations_count: number
}

interface WhatsappConnection {
  business_type: string
  phone_number: string
  verified_name: string
  quality_rating: string | null
  conversations_count: number
}

interface YoutubeConnection {
  id: number
  channel_title: string
  subscriber_count: number
  video_count: number
  profile_picture_url: string | null
  token_expires_at: string
  videos_count: number
}

interface GoogleCalendarConnection {
  business_type: string
  google_account_email: string
  last_synced_at: string | null
  sync_enabled: boolean
  webhook_expires_at: string | null
  connected_at: string
}

interface Integration {
  id: string
  name: string
  description: string
  connected: boolean
  credentials: {
    [key: string]: string | null
  }
  instagram_accounts?: InstagramAccount[]
  messenger_connections?: MessengerConnection[]
  whatsapp_connections?: WhatsappConnection[]
  youtube_connection?: YoutubeConnection
  google_calendar_connections?: GoogleCalendarConnection[]
}

interface IntegrationsIndexProps {
  integrations: Integration[]
  instagram_available_business_types: string[]
  messenger_available_business_types: string[]
  whatsapp_available_business_types: string[]
  google_calendar_available_business_types: string[]
}

export default function IntegrationsIndex({ integrations, instagram_available_business_types, messenger_available_business_types, whatsapp_available_business_types, google_calendar_available_business_types }: IntegrationsIndexProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [businessTypeSelectorOpen, setBusinessTypeSelectorOpen] = useState(false)
  const [platformForBusinessTypeSelector, setPlatformForBusinessTypeSelector] = useState<'instagram' | 'facebook_messenger' | 'whatsapp' | 'google_calendar' | null>(null)
  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<InstagramAccount | null>(null)
  const [selectedConnection, setSelectedConnection] = useState<MessengerConnection | null>(null)
  const [selectedWhatsappConnection, setSelectedWhatsappConnection] = useState<WhatsappConnection | null>(null)
  const [selectedBusinessType, setSelectedBusinessType] = useState<string | null>(null)
  const [formData, setFormData] = useState<{ [key: string]: string }>({})
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [syncing, setSyncing] = useState(false)

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'instagram_oauth_callback' || event.data.type === 'messenger_oauth_callback' || event.data.type === 'youtube_oauth_callback' || event.data.type === 'google_calendar_oauth_callback') {
        if (event.data.success) {
          showSuccess(event.data.message)
          // Reload the page to show updated integration status
          router.reload()
        } else {
          showError(event.data.message)
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const getIcon = (id: string) => {
    switch (id) {
      case 'instagram':
        return InstagramIcon
      case 'facebook_messenger':
        return FacebookIcon
      case 'whatsapp':
        return MessageCircleIcon
      case 'telegram':
        return SendIcon
      case 'youtube':
        return YoutubeIcon
      case 'google_calendar':
        return CalendarIcon
      default:
        return PlugIcon
    }
  }

  const getIconColor = (id: string) => {
    switch (id) {
      case 'instagram':
        return 'text-pink-600'
      case 'facebook_messenger':
        return 'text-blue-600'
      case 'whatsapp':
        return 'text-green-600'
      case 'telegram':
        return 'text-sky-600'
      case 'youtube':
        return 'text-red-600'
      case 'google_calendar':
        return 'text-blue-500'
      default:
        return 'text-gray-600'
    }
  }

  const getBusinessTypeIcon = (businessType: string) => {
    return businessType === 'shop' ? StoreIcon : TruckIcon
  }

  const getBusinessTypeBadgeColor = (businessType: string) => {
    return businessType === 'shop' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-orange-100 text-orange-700 border-orange-300'
  }

  const getFormFields = (id: string): Array<{ name: string; label: string; placeholder: string; required: boolean }> => {
    switch (id) {
      case 'facebook_messenger':
        return [
          { name: 'page_id', label: 'Page ID', placeholder: 'Your Facebook Page ID', required: true },
          { name: 'access_token', label: 'Access Token', placeholder: 'Enter your Facebook access token', required: true },
        ]
      case 'whatsapp':
        return [
          { name: 'phone_number_id', label: 'Phone Number ID', placeholder: '799504826590746', required: true },
          { name: 'business_account_id', label: 'Business Account ID', placeholder: '529217830287021', required: false },
          { name: 'access_token', label: 'Access Token', placeholder: 'Enter your WhatsApp Business API access token', required: true },
          { name: 'webhook_verify_token', label: 'Webhook Verify Token', placeholder: 'my_secure_verify_token_123', required: true },
        ]
      case 'telegram':
        return [
          { name: 'bot_token', label: 'Bot Token', placeholder: 'Enter your Telegram bot token', required: true },
          { name: 'chat_id', label: 'Chat ID', placeholder: 'Enter your chat ID', required: true },
        ]
      case 'youtube':
        return [
          { name: 'channel_id', label: 'Channel ID', placeholder: 'Your YouTube channel ID', required: true },
          { name: 'api_key', label: 'API Key', placeholder: 'Enter your YouTube API key', required: true },
        ]
      default:
        return []
    }
  }

  const handleConnect = async (integration: Integration) => {
    // Instagram, Messenger, and WhatsApp need business type selection
    if (integration.id === 'instagram' || integration.id === 'facebook_messenger' || integration.id === 'whatsapp') {
      setSelectedIntegration(integration)
      setPlatformForBusinessTypeSelector(integration.id as 'instagram' | 'facebook_messenger' | 'whatsapp')
      setBusinessTypeSelectorOpen(true)
      return
    }

    // YouTube uses OAuth (no business type selection)
    if (integration.id === 'youtube') {
      try {
        // Fetch OAuth URL from backend
        fetch('/youtube/oauth/authorize', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          }
        })
          .then(response => response.json())
          .then(data => {
            // Open OAuth popup
            const width = 600
            const height = 700
            const left = window.screen.width / 2 - width / 2
            const top = window.screen.height / 2 - height / 2

            window.open(
              data.oauth_url,
              'youtube_oauth',
              `width=${width},height=${height},left=${left},top=${top}`
            )
          })
          .catch(() => {
            showError('Failed to start YouTube authorization')
          })
      } catch (error) {
        showError('Failed to start YouTube authorization')
      }
      return
    }

    // Google Calendar uses OAuth (no business type selection)
    if (integration.id === 'google_calendar') {
      try {
        // Fetch OAuth URL from backend
        fetch('/google_calendar/oauth/authorize', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          }
        })
          .then(response => response.json())
          .then(data => {
            // Open OAuth popup
            const width = 600
            const height = 700
            const left = window.screen.width / 2 - width / 2
            const top = window.screen.height / 2 - height / 2

            window.open(
              data.oauth_url,
              'google_calendar_oauth',
              `width=${width},height=${height},left=${left},top=${top}`
            )
          })
          .catch(() => {
            showError('Failed to start Google Calendar authorization')
          })
      } catch (error) {
        showError('Failed to start Google Calendar authorization')
      }
      return
    }

    // Other integrations use credentials dialog
    setSelectedIntegration(integration)
    setFormData({})
    setErrors({})
    setDialogOpen(true)
  }

  const handleBusinessTypeSelected = async (businessType: string) => {
    setBusinessTypeSelectorOpen(false)

    if (!platformForBusinessTypeSelector) return

    // WhatsApp uses credentials dialog, not OAuth
    if (platformForBusinessTypeSelector === 'whatsapp') {
      setSelectedBusinessType(businessType)
      setFormData({})
      setErrors({})
      setDialogOpen(true)
      return
    }

    try {
      // Determine which OAuth endpoint to use
      const oauthPath = platformForBusinessTypeSelector === 'instagram'
        ? '/instagram/oauth/authorize'
        : platformForBusinessTypeSelector === 'google_calendar'
          ? '/google_calendar/oauth/authorize'
          : '/messenger/oauth/authorize'

      const platformName = platformForBusinessTypeSelector === 'instagram'
        ? 'Instagram'
        : platformForBusinessTypeSelector === 'google_calendar'
          ? 'Google Calendar'
          : 'Messenger'

      // Fetch OAuth URL from backend with business_type parameter
      const response = await fetch(`${oauthPath}?business_type=${businessType}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        }
      })
      const data = await response.json()

      // Open OAuth popup
      const width = 600
      const height = 700
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const windowName = platformForBusinessTypeSelector === 'instagram'
        ? 'instagram_oauth'
        : platformForBusinessTypeSelector === 'google_calendar'
          ? 'google_calendar_oauth'
          : 'messenger_oauth'

      window.open(
        data.oauth_url,
        windowName,
        `width=${width},height=${height},left=${left},top=${top}`
      )
    } catch (error) {
      const platformName = platformForBusinessTypeSelector === 'instagram'
        ? 'Instagram'
        : platformForBusinessTypeSelector === 'google_calendar'
          ? 'Google Calendar'
          : 'Messenger'
      showError(`Failed to start ${platformName} authorization`)
    }
  }

  const handleManage = (account: InstagramAccount) => {
    setSelectedAccount(account)
    setManageDialogOpen(true)
  }

  const handleManageMessenger = (connection: MessengerConnection) => {
    setSelectedConnection(connection)
    setManageDialogOpen(true)
  }

  const handleManageWhatsapp = (connection: WhatsappConnection) => {
    setSelectedWhatsappConnection(connection)
    setManageDialogOpen(true)
  }

  const handleDisconnectClick = (integration: Integration, account?: InstagramAccount, connection?: MessengerConnection, whatsappConnection?: WhatsappConnection) => {
    setSelectedIntegration(integration)
    setSelectedAccount(account || null)
    setSelectedConnection(connection || null)
    setSelectedWhatsappConnection(whatsappConnection || null)
    setDisconnectDialogOpen(true)
  }

  const handleDisconnectConfirm = () => {
    if (!selectedIntegration) return

    const params: any = { platform: selectedIntegration.id }
    if (selectedAccount) {
      params.business_type = selectedAccount.business_type
    }
    if (selectedConnection) {
      params.business_type = selectedConnection.business_type
    }
    if (selectedWhatsappConnection) {
      params.business_type = selectedWhatsappConnection.business_type
    }
    if (selectedBusinessType) {
      params.business_type = selectedBusinessType
    }

    router.post('/admin/integrations/disconnect', params, {
      onSuccess: () => {
        setDisconnectDialogOpen(false)
        setSelectedIntegration(null)
        setSelectedAccount(null)
        setSelectedConnection(null)
        setSelectedWhatsappConnection(null)
        setSelectedBusinessType(null)
      }
    })
  }

  const handleSync = async (platform: 'instagram' | 'messenger') => {
    setSyncing(true)
    try {
      const syncPath = platform === 'instagram' ? '/instagram/sync' : '/messenger/sync'
      const platformName = platform === 'instagram' ? 'Instagram' : 'Messenger'

      const token = localStorage.getItem('auth_token')
      if (!token) {
        showError('Authentication required')
        return
      }

      const response = await fetch(syncPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Authorization': `Bearer ${token}`,
        }
      })
      const data = await response.json()

      if (data.success) {
        showSuccess(data.message)
      } else {
        showError(data.message)
      }
    } catch (error) {
      const platformName = platform === 'instagram' ? 'Instagram' : 'Messenger'
      showError(`Failed to sync ${platformName} conversations`)
    } finally {
      setSyncing(false)
    }
  }

  const validateForm = (): boolean => {
    if (!selectedIntegration) return false

    const fields = getFormFields(selectedIntegration.id)
    const newErrors: { [key: string]: string } = {}

    fields.forEach(field => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.label} is required`
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || !selectedIntegration) return

    // WhatsApp uses direct API call instead of Inertia router
    if (selectedIntegration.id === 'whatsapp') {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('/whatsapp/connect', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            phone_number_id: formData.phone_number_id,
            business_account_id: formData.business_account_id,
            access_token: formData.access_token,
            webhook_verify_token: formData.webhook_verify_token,
            business_type: selectedBusinessType
          }),
        })

        const data = await response.json()

        if (response.ok && data.success) {
          showSuccess('WhatsApp has been connected successfully.')
          setDialogOpen(false)
          setSelectedIntegration(null)
          setSelectedBusinessType(null)
          setFormData({})
          router.reload()
        } else {
          showError(data.errors?.join(', ') || 'Failed to connect WhatsApp')
        }
      } catch (error) {
        showError('Failed to connect WhatsApp')
      }
      return
    }

    // Other platforms use Inertia router
    router.post('/admin/integrations/connect',
      {
        platform: selectedIntegration.id,
        credentials: formData
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          showSuccess(`${selectedIntegration.name} has been connected successfully.`)
          setDialogOpen(false)
          setSelectedIntegration(null)
          setFormData({})
        },
        onError: (errors) => {
          if (errors && typeof errors === 'object') {
            showError(Object.values(errors).flat().join(', '))
          } else {
            showError('Failed to connect integration')
          }
        }
      }
    )
  }

  return (
    <>
      <div className="flex flex-1 flex-col min-w-0">
        <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
            {/* Header */}
            <div className="px-4 lg:px-6">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
                <p className="text-muted-foreground">Connect your social media accounts to manage leads from multiple channels</p>
              </div>
            </div>

            {/* Integrations Grid */}
            <div className="px-4 lg:px-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {integrations.map((integration) => {
                  const Icon = getIcon(integration.id)
                  const iconColor = getIconColor(integration.id)

                  return (
                    <Card key={integration.id} className="relative" data-testid={`integration-card-${integration.id}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`flex size-12 items-center justify-center rounded-lg bg-muted`}>
                              <Icon className={`size-6 ${iconColor}`} />
                            </div>
                            <div>
                              <CardTitle>{integration.name}</CardTitle>
                              {integration.connected && (
                                <Badge variant="outline" className="mt-1 text-green-600 border-green-600">
                                  <CheckCircleIcon className="mr-1 size-3" />
                                  Connected
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">
                          {integration.description}
                        </CardDescription>

                        {/* Instagram with multiple accounts */}
                        {integration.id === 'instagram' && integration.instagram_accounts && integration.instagram_accounts.length > 0 ? (
                          <div className="space-y-3">
                            {integration.instagram_accounts.map((account) => {
                              const BusinessTypeIcon = getBusinessTypeIcon(account.business_type)
                              const badgeColor = getBusinessTypeBadgeColor(account.business_type)

                              return (
                                <div key={account.business_type} className="flex items-center gap-4 p-3 border rounded-lg min-w-0">
                                  <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                                    {account.profile_picture_url && (
                                      <img
                                        src={account.profile_picture_url}
                                        alt={account.username}
                                        className="size-10 rounded-full flex-shrink-0"
                                      />
                                    )}
                                    <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                                      <p className="font-medium text-sm truncate">@{account.username}</p>
                                      <Badge variant="outline" className={`text-xs ${badgeColor} w-fit`}>
                                        <BusinessTypeIcon className="mr-1 size-3" />
                                        {account.business_type === 'shop' ? 'Shop' : 'Recruiting'}
                                      </Badge>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleManage(account)}
                                    data-testid={`manage-button-${account.business_type}`}
                                    className="bg-[#84b9ef] hover:bg-[#6fa8e0] shrink-0 min-w-[80px]"
                                  >
                                    Manage
                                  </Button>
                                </div>
                              )
                            })}

                            {/* Show Connect button if slots available */}
                            {instagram_available_business_types.length > 0 && (
                              <Button
                                size="sm"
                                onClick={() => handleConnect(integration)}
                                className="w-full bg-[#84b9ef] hover:bg-[#6fa8e0]"
                                data-testid="connect-additional-button"
                              >
                                Connect ({instagram_available_business_types.length} slot{instagram_available_business_types.length !== 1 ? 's' : ''} available)
                              </Button>
                            )}
                          </div>
                        ) : integration.id === 'facebook_messenger' && integration.messenger_connections && integration.messenger_connections.length > 0 ? (
                          <div className="space-y-3">
                            {integration.messenger_connections.map((connection) => {
                              const BusinessTypeIcon = getBusinessTypeIcon(connection.business_type)
                              const badgeColor = getBusinessTypeBadgeColor(connection.business_type)

                              return (
                                <div key={connection.business_type} className="flex items-center gap-4 p-3 border rounded-lg min-w-0">
                                  <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                                    {connection.profile_picture_url && (
                                      <img
                                        src={connection.profile_picture_url}
                                        alt={connection.page_name}
                                        className="size-10 rounded-full flex-shrink-0"
                                      />
                                    )}
                                    <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                                      <p className="font-medium text-sm truncate">{connection.page_name}</p>
                                      <Badge variant="outline" className={`text-xs ${badgeColor} w-fit`}>
                                        <BusinessTypeIcon className="mr-1 size-3" />
                                        {connection.business_type === 'shop' ? 'Shop' : 'Recruiting'}
                                      </Badge>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleManageMessenger(connection)}
                                    data-testid={`manage-button-${connection.business_type}`}
                                    className="bg-[#0084ff] hover:bg-[#006bd6] shrink-0 min-w-[80px]"
                                  >
Manage
                                  </Button>
                                </div>
                              )
                            })}

                            {/* Show Connect button if slots available */}
                            {messenger_available_business_types.length > 0 && (
                              <Button
                                size="sm"
                                onClick={() => handleConnect(integration)}
                                className="w-full bg-[#0084ff] hover:bg-[#006bd6]"
                                data-testid="connect-additional-button"
                              >
                                Connect ({messenger_available_business_types.length} slot{messenger_available_business_types.length !== 1 ? 's' : ''} available)
                              </Button>
                            )}
                          </div>
                        ) : integration.id === 'whatsapp' && integration.whatsapp_connections && integration.whatsapp_connections.length > 0 ? (
                          <div className="space-y-3">
                            {integration.whatsapp_connections.map((connection) => {
                              const BusinessTypeIcon = getBusinessTypeIcon(connection.business_type)
                              const badgeColor = getBusinessTypeBadgeColor(connection.business_type)

                              return (
                                <div key={connection.business_type} className="flex items-center gap-4 p-3 border rounded-lg min-w-0">
                                  <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                                    <div className="flex size-10 items-center justify-center rounded-full bg-[#25D366] text-white flex-shrink-0">
                                      <MessageCircleIcon className="size-6" />
                                    </div>
                                    <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                                      <p className="font-medium text-sm truncate">{connection.phone_number}</p>
                                      <Badge variant="outline" className={`text-xs ${badgeColor} w-fit`}>
                                        <BusinessTypeIcon className="mr-1 size-3" />
                                        {connection.business_type === 'shop' ? 'Shop' : 'Recruiting'}
                                      </Badge>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleManageWhatsapp(connection)}
                                    data-testid={`manage-button-${connection.business_type}`}
                                    className="bg-[#25D366] hover:bg-[#1da851] shrink-0 min-w-[80px]"
                                  >
                                    Manage
                                  </Button>
                                </div>
                              )
                            })}

                            {/* Show Connect button if slots available */}
                            {whatsapp_available_business_types.length > 0 && (
                              <Button
                                size="sm"
                                onClick={() => handleConnect(integration)}
                                className="w-full bg-[#25D366] hover:bg-[#1da851]"
                                data-testid="connect-additional-button"
                              >
                                Connect ({whatsapp_available_business_types.length} slot{whatsapp_available_business_types.length !== 1 ? 's' : ''} available)
                              </Button>
                            )}
                          </div>
                        ) : integration.id === 'youtube' && integration.youtube_connection ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-4 p-3 border rounded-lg min-w-0">
                              <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                                {integration.youtube_connection.profile_picture_url ? (
                                  <img
                                    src={integration.youtube_connection.profile_picture_url}
                                    alt={integration.youtube_connection.channel_title}
                                    className="size-10 rounded-full flex-shrink-0"
                                  />
                                ) : (
                                  <div className="flex size-10 items-center justify-center rounded-full bg-red-600 text-white flex-shrink-0">
                                    <YoutubeIcon className="size-6" />
                                  </div>
                                )}
                                <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                                  <p className="font-medium text-sm truncate">{integration.youtube_connection.channel_title}</p>
                                  <div className="flex gap-2 text-xs text-muted-foreground">
                                    <span>{integration.youtube_connection.subscriber_count.toLocaleString()} subscribers</span>
                                    <span>•</span>
                                    <span>{integration.youtube_connection.videos_count} videos</span>
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDisconnectClick(integration)}
                                className="shrink-0 min-w-[100px]"
                                data-testid="disconnect-youtube-button"
                              >
                                Disconnect
                              </Button>
                            </div>
                          </div>
                        ) : integration.id === 'google_calendar' && integration.google_calendar_connections && integration.google_calendar_connections.length > 0 ? (
                          <div className="space-y-3">
                            {integration.google_calendar_connections.map((connection) => (
                              <div key={connection.business_type} className="flex items-center gap-4 p-3 border rounded-lg min-w-0">
                                <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                                  <div className="flex size-10 items-center justify-center rounded-full bg-blue-500 text-white flex-shrink-0">
                                    <CalendarIcon className="size-6" />
                                  </div>
                                  <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-sm truncate">{connection.google_account_email}</p>
                                      <Badge variant="outline" className="shrink-0">
                                        {connection.business_type === 'shop' && <StoreIcon className="size-3 mr-1" />}
                                        {connection.business_type === 'recruiting' && <TruckIcon className="size-3 mr-1" />}
                                        {connection.business_type === 'shop' ? 'Shop' : 'Recruiting'}
                                      </Badge>
                                    </div>
                                    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                                      <div className="flex gap-2 items-center">
                                        <span>Last synced: {connection.last_synced_at ? new Date(connection.last_synced_at).toLocaleString() : 'Never'}</span>
                                      </div>
                                      <div className="flex gap-2 items-center">
                                        <span>Connected: {connection.connected_at ? new Date(connection.connected_at).toLocaleDateString() : 'Unknown'}</span>
                                      </div>
                                      {connection.webhook_expires_at && (
                                        <div className="flex gap-2 items-center">
                                          <span>Webhook expires: {new Date(connection.webhook_expires_at).toLocaleDateString()}</span>
                                        </div>
                                      )}
                                      <div className="flex gap-2 items-center">
                                        <span className={connection.sync_enabled ? 'text-green-600' : 'text-orange-600'}>
                                          Sync: {connection.sync_enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedIntegration(integration)
                                    setSelectedBusinessType(connection.business_type)
                                    setDisconnectDialogOpen(true)
                                  }}
                                  className="shrink-0 min-w-[100px]"
                                  data-testid={`disconnect-google-calendar-${connection.business_type}-button`}
                                >
                                  Disconnect
                                </Button>
                              </div>
                            ))}
                            {google_calendar_available_business_types.length > 0 && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setPlatformForBusinessTypeSelector('google_calendar' as any)
                                  setBusinessTypeSelectorOpen(true)
                                }}
                                className="w-full bg-[#84b9ef] hover:bg-[#6fa8e0]"
                                data-testid="connect-google-calendar-additional-button"
                              >
                                Connect ({google_calendar_available_business_types.length} slot{google_calendar_available_business_types.length > 1 ? 's' : ''} available)
                              </Button>
                            )}
                          </div>
                        ) : integration.connected ? (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDisconnectClick(integration)}
                              className="w-full"
                              data-testid={`disconnect-button-${integration.id}`}
                            >
                              Disconnect
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleConnect(integration)}
                            className="w-full bg-[#84b9ef] hover:bg-[#6fa8e0]"
                            data-testid={`connect-button-${integration.id}`}
                          >
                            Connect
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Type Selector Dialog */}
      <Dialog open={businessTypeSelectorOpen} onOpenChange={setBusinessTypeSelectorOpen}>
        <DialogContent data-testid="business-type-selector-dialog">
          <DialogHeader>
            <DialogTitle>Select Account Type</DialogTitle>
            <DialogDescription>
              Choose which type of business this {platformForBusinessTypeSelector === 'instagram' ? 'Instagram account' : platformForBusinessTypeSelector === 'whatsapp' ? 'WhatsApp Business account' : platformForBusinessTypeSelector === 'google_calendar' ? 'Google Calendar' : 'Messenger page'} will be used for
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {(platformForBusinessTypeSelector === 'instagram'
              ? instagram_available_business_types
              : platformForBusinessTypeSelector === 'whatsapp'
                ? whatsapp_available_business_types
                : platformForBusinessTypeSelector === 'google_calendar'
                  ? google_calendar_available_business_types
                  : messenger_available_business_types
            ).map((businessType) => {
              const BusinessTypeIcon = getBusinessTypeIcon(businessType)
              const isShop = businessType === 'shop'
              const hoverColor = platformForBusinessTypeSelector === 'instagram'
                ? 'hover:border-[#84b9ef]'
                : platformForBusinessTypeSelector === 'whatsapp'
                  ? 'hover:border-[#25D366]'
                  : platformForBusinessTypeSelector === 'google_calendar'
                    ? 'hover:border-blue-500'
                    : 'hover:border-[#0084ff]'

              return (
                <button
                  key={businessType}
                  onClick={() => handleBusinessTypeSelected(businessType)}
                  className={`w-full p-4 border-2 rounded-lg ${hoverColor} hover:bg-muted/50 transition-colors text-left`}
                  data-testid={`select-${businessType}-button`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex size-12 items-center justify-center rounded-lg ${isShop ? 'bg-blue-100' : 'bg-orange-100'}`}>
                      <BusinessTypeIcon className={`size-6 ${isShop ? 'text-blue-600' : 'text-orange-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold">{isShop ? 'Shop / Repair' : 'Driver Recruiting'}</p>
                      <p className="text-sm text-muted-foreground">
                        {isShop ? 'For maintenance, appointments, and shop leads' : 'For driver recruitment and hiring'}
                      </p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBusinessTypeSelectorOpen(false)}
              data-testid="cancel-business-type-button"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="connection-dialog">
          <DialogHeader>
            <DialogTitle>Connect {selectedIntegration?.name}</DialogTitle>
            <DialogDescription>
              Enter your {selectedIntegration?.name} credentials to connect your account
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedIntegration && getFormFields(selectedIntegration.id).map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name}>
                  {field.label} {field.required && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, [field.name]: e.target.value })
                    if (errors[field.name]) {
                      setErrors({ ...errors, [field.name]: '' })
                    }
                  }}
                  data-testid={`input-${field.name}`}
                />
                {errors[field.name] && (
                  <p className="text-sm text-destructive">{errors[field.name]}</p>
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-[#84b9ef] hover:bg-[#6fa8e0]"
              data-testid="submit-button"
            >
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instagram/Messenger/WhatsApp Manage Dialog */}
      <Dialog open={manageDialogOpen} onOpenChange={setManageDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="manage-dialog">
          <DialogHeader>
            <DialogTitle>Manage {selectedAccount ? 'Instagram Account' : selectedWhatsappConnection ? 'WhatsApp Business Account' : 'Messenger Page'}</DialogTitle>
            <DialogDescription>
              View your connected {selectedAccount ? 'Instagram account' : selectedWhatsappConnection ? 'WhatsApp Business account' : 'Messenger page'} details and manage settings
            </DialogDescription>
          </DialogHeader>

          {selectedAccount && (
            <div className="space-y-4 py-4">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                {selectedAccount.profile_picture_url && (
                  <img
                    src={selectedAccount.profile_picture_url}
                    alt={selectedAccount.username}
                    className="size-16 rounded-full flex-shrink-0"
                  />
                )}
                <div className="text-center sm:text-left flex-1 min-w-0">
                  <p className="font-semibold text-lg break-words">@{selectedAccount.username}</p>
                  <p className="text-sm text-muted-foreground capitalize">{selectedAccount.account_type}</p>
                  <Badge variant="outline" className={`mt-1 ${getBusinessTypeBadgeColor(selectedAccount.business_type)}`}>
                    {selectedAccount.business_type === 'shop' ? 'Shop' : 'Recruiting'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm font-medium">Conversations</span>
                  <span className="text-sm text-muted-foreground">{selectedAccount.conversations_count}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm font-medium">Token Expires</span>
                  <span className="text-sm text-muted-foreground">
                    {new Date(selectedAccount.token_expires_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  onClick={() => handleSync('instagram')}
                  disabled={syncing}
                  className="w-full bg-[#84b9ef] hover:bg-[#6fa8e0]"
                  data-testid="sync-button"
                >
                  {syncing ? 'Syncing...' : 'Sync Conversations'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Manually sync to fetch latest messages from Instagram
                </p>
              </div>
            </div>
          )}

          {selectedConnection && (
            <div className="space-y-4 py-4">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                {selectedConnection.profile_picture_url && (
                  <img
                    src={selectedConnection.profile_picture_url}
                    alt={selectedConnection.page_name}
                    className="size-16 rounded-full flex-shrink-0"
                  />
                )}
                <div className="text-center sm:text-left flex-1 min-w-0">
                  <p className="font-semibold text-lg break-words">{selectedConnection.page_name}</p>
                  {selectedConnection.page_username && (
                    <p className="text-sm text-muted-foreground break-words">@{selectedConnection.page_username}</p>
                  )}
                  <Badge variant="outline" className={`mt-1 ${getBusinessTypeBadgeColor(selectedConnection.business_type)}`}>
                    {selectedConnection.business_type === 'shop' ? 'Shop' : 'Recruiting'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm font-medium">Conversations</span>
                  <span className="text-sm text-muted-foreground">{selectedConnection.conversations_count}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm font-medium">Token Expires</span>
                  <span className="text-sm text-muted-foreground">
                    {new Date(selectedConnection.token_expires_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  onClick={() => handleSync('messenger')}
                  disabled={syncing}
                  className="w-full bg-[#0084ff] hover:bg-[#006bd6]"
                  data-testid="sync-button"
                >
                  {syncing ? 'Syncing...' : 'Sync Conversations'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Manually sync to fetch latest messages from Messenger
                </p>
              </div>
            </div>
          )}

          {selectedWhatsappConnection && (
            <div className="space-y-4 py-4">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                <div className="flex size-16 items-center justify-center rounded-full bg-[#25D366] text-white flex-shrink-0">
                  <MessageCircleIcon className="size-8" />
                </div>
                <div className="text-center sm:text-left flex-1 min-w-0">
                  <p className="font-semibold text-lg break-words">{selectedWhatsappConnection.phone_number}</p>
                  <p className="text-sm text-muted-foreground break-words">{selectedWhatsappConnection.verified_name}</p>
                  <Badge variant="outline" className={`mt-1 ${getBusinessTypeBadgeColor(selectedWhatsappConnection.business_type)}`}>
                    {selectedWhatsappConnection.business_type === 'shop' ? 'Shop' : 'Recruiting'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm font-medium">Conversations</span>
                  <span className="text-sm text-muted-foreground">{selectedWhatsappConnection.conversations_count}</span>
                </div>
                {selectedWhatsappConnection.quality_rating && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm font-medium">Quality Rating</span>
                    <span className="text-sm text-muted-foreground capitalize">{selectedWhatsappConnection.quality_rating}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="destructive"
              onClick={() => {
                setManageDialogOpen(false)
                if (selectedAccount) {
                  handleDisconnectClick({ id: 'instagram', name: 'Instagram', description: '', connected: true }, selectedAccount)
                }
                if (selectedConnection) {
                  handleDisconnectClick({ id: 'facebook_messenger', name: 'Facebook Messenger', description: '', connected: true }, undefined, selectedConnection)
                }
                if (selectedWhatsappConnection) {
                  handleDisconnectClick({ id: 'whatsapp', name: 'WhatsApp', description: '', connected: true }, undefined, undefined, selectedWhatsappConnection)
                }
              }}
              data-testid="disconnect-from-manage-button"
              className="w-full sm:w-auto"
            >
              <TrashIcon className="mr-2 size-4" />
              Disconnect
            </Button>
            <Button
              variant="outline"
              onClick={() => setManageDialogOpen(false)}
              data-testid="close-button"
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {selectedIntegration?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedAccount ? (
                <>
                  This will disconnect the <strong>@{selectedAccount.username}</strong> ({selectedAccount.business_type}) account.
                  All associated conversations and messages will be preserved but you won't be able to send new messages.
                </>
              ) : selectedConnection ? (
                <>
                  This will disconnect the <strong>{selectedConnection.page_name}</strong> ({selectedConnection.business_type}) page.
                  All associated conversations and messages will be preserved but you won't be able to send new messages.
                </>
              ) : selectedWhatsappConnection ? (
                <>
                  This will disconnect the <strong>{selectedWhatsappConnection.phone_number}</strong> ({selectedWhatsappConnection.business_type}) WhatsApp Business account.
                  All associated conversations and messages will be preserved but you won't be able to send new messages.
                </>
              ) : (
                <>
                  This will disconnect your {selectedIntegration?.name} account.
                  All associated data will be preserved but you won't be able to access this integration.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnectConfirm} className="bg-destructive hover:bg-destructive/90">
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
